import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface CalendarBookingProps {
  onConfirm: (date: Date, time: string) => void;
  onBack: () => void;
}
export function CalendarBooking({
  onConfirm,
  onBack
}: CalendarBookingProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const timeSlots = ['8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'];
  // Simulate booked slots based on date (seeded randomization for consistency)
  const getBookedSlots = (date: Date): string[] => {
    const seed = date.getDate() + date.getMonth() * 31;
    const bookedIndices = [seed * 3 % timeSlots.length, seed * 7 % timeSlots.length, seed * 11 % timeSlots.length, seed * 13 % timeSlots.length];
    return bookedIndices.map(i => timeSlots[i]);
  };
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return {
      daysInMonth,
      startingDayOfWeek
    };
  };
  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };
  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const isTooFarOut = (date: Date) => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);
    return date > maxDate;
  };
  const isDateDisabled = (date: Date) => {
    return isWeekend(date) || isPastDate(date) || isTooFarOut(date);
  };
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    setSelectedDate(null);
    setSelectedTime(null);
  };
  const handleDateSelect = (date: Date) => {
    if (!isDateDisabled(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };
  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      setIsConfirming(true);
      setTimeout(() => {
        onConfirm(selectedDate, selectedTime);
      }, 800);
    }
  };
  const {
    daysInMonth,
    startingDayOfWeek
  } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleString('en-AU', {
    month: 'long',
    year: 'numeric'
  });
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }
  const bookedSlots = selectedDate ? getBookedSlots(selectedDate) : [];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.3
  }} className="space-y-6" data-id="element-54">
      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 rounded-lg p-3" data-id="element-55">
        <InfoIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" data-id="element-56" />
        <span data-id="element-57">Duration: 30 minutes | Complimentary strategy session</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-58">
        {/* Calendar */}
        <div data-id="element-59">
          <div className="flex items-center justify-between mb-4" data-id="element-60">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white" data-id="element-61">
              {monthName}
            </h3>
            <div className="flex gap-2" data-id="element-62">
              <button onClick={handlePrevMonth} className="p-2 rounded-lg bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-all" aria-label="Previous month" data-id="element-63">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-64" />
              </button>
              <button onClick={handleNextMonth} className="p-2 rounded-lg bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-all" aria-label="Next month" data-id="element-65">
                <ChevronRightIcon className="w-5 h-5" data-id="element-66" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2" data-id="element-67">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-xs font-medium text-slate-500 pb-2" data-id="element-68">
                {day}
              </div>)}
            {calendarDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} data-id="element-69" />;
            }
            const disabled = isDateDisabled(date);
            const isSelected = selectedDate?.toDateString() === date.toDateString();
            return <button key={date.toISOString()} onClick={() => handleDateSelect(date)} disabled={disabled} className={`aspect-square rounded-lg text-sm font-medium transition-all ${disabled ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : isSelected ? 'bg-gradient-to-r from-accent-500 to-primary-500 text-white shadow-[0_0_15px_rgba(20,184,166,0.4)]' : 'text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/20'}`} data-id="element-70">
                  {date.getDate()}
                </button>;
          })}
          </div>
        </div>

        {/* Time Slots */}
        <div data-id="element-71">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-72">
            {selectedDate ? `Available Times - ${selectedDate.toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
          })}` : 'Select a date to view times'}
          </h3>

          {selectedDate ? <div className="space-y-2 max-h-96 overflow-y-auto pr-2" data-id="element-73">
              {timeSlots.map(time => {
            const isBooked = bookedSlots.includes(time);
            const isSelected = selectedTime === time;
            return <button key={time} onClick={() => !isBooked && setSelectedTime(time)} disabled={isBooked} className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${isBooked ? 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-600 cursor-not-allowed border border-slate-200 dark:border-white/5' : isSelected ? 'bg-gradient-to-r from-accent-500 to-primary-500 text-white shadow-[0_0_15px_rgba(20,184,166,0.4)]' : 'bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-white/10'}`} data-id="element-74">
                    <span className="flex items-center gap-2" data-id="element-75">
                      <ClockIcon className="w-4 h-4" data-id="element-76" />
                      {time}
                    </span>
                    {isBooked && <span className="text-xs" data-id="element-77">Booked</span>}
                  </button>;
          })}
              <p className="text-xs text-slate-500 text-center pt-2" data-id="element-78">
                All times shown in AWST (Perth time)
              </p>
            </div> : <div className="flex items-center justify-center h-64 text-slate-500 text-sm" data-id="element-79">
              Please select a date to view available times
            </div>}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4" data-id="element-80">
        <button onClick={onBack} className="px-6 py-3 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-medium transition-all flex items-center gap-2" data-id="element-81">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-82" />
          Back
        </button>
        <button onClick={handleConfirm} disabled={!selectedDate || !selectedTime || isConfirming} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-83">
          {isConfirming ? 'Confirming...' : 'Confirm Booking'}
        </button>
      </div>
    </motion.div>;
}